import { Injectable } from '@angular/core';

import { Certificate } from 'src/app/shared/classes/certificate';
import { LacunaWebPkiService } from 'src/app/shared/components/lacuna-web-pki/lacuna-web-pki.service';
import { LoadingSignatureService } from 'src/app/shared/components/settings/loading-signature/loading-signature.service';
import { SignStatusEnum } from 'src/app/shared/enum/signStatusEnum';
import { TipoPessoaLetraEnum } from 'src/app/shared/enum/tipoPessoaEnum';
import SignatureDocumentDigital from 'src/app/shared/models/signatureDocmentDigital';
import SignatureInvolved from 'src/app/shared/models/signatureInvolved';
import { SignatureHttpService } from '../signature.http.service';
import { ModoAssinaturaDigital } from 'src/app/shared/enum/modo-assinatura-digital.enum';

interface ILogEvent {
  method: string;
  tipo: 'ENTRADA' | 'SAIDA' | 'ERRO';
  returnData?: any;
  params: any;
  error?: any;
}

class SignatureDigitalServiceLog {
  public events: ILogEvent[] = [];

  public addEvent(event: ILogEvent): void {
    this.events.push(event);
  }

  public printEvent(): void {
    console.log('####################### ASSINATURA DIGITAL LOG #######################');
    console.log(this.events);
    console.log('######################################################################');
  }

  public clearAll(): void {
    this.events = [];
  }
}

@Injectable()
export class SignatureDigitalService {
  private log: SignatureDigitalServiceLog = new SignatureDigitalServiceLog();

  constructor(
    private signatureHttpService: SignatureHttpService,
    private lacunaService: LacunaWebPkiService,
    private loadingSignatureService: LoadingSignatureService,
  ) { }

  public async execute(pDocumentosComInvolvido: SignatureDocumentDigital[], pCertificado: Certificate, pModoAssinatura: ModoAssinaturaDigital): Promise<void> {
    this.log.addEvent({
      method: 'execute',
      tipo: 'ENTRADA',
      params: {
        pDocumentosComInvolvido,
        pCertificado,
        pModoAssinatura,
      },
    });
    try {
      if (pCertificado.isExpired()) {
        throw new Error('Certificado vencido');
      }

      const documentosAssinar = this.getDocumentosAssinarDigital(pDocumentosComInvolvido, pCertificado);
      const qtdTotalAssinatura = this.getQtdAssinaturasTotal(documentosAssinar);
      if (qtdTotalAssinatura === 0) {
        console.log('Involveds:', pDocumentosComInvolvido)
        console.log('Certificado: ', pCertificado)
      }

      this.loadingSignatureService.showLoader('DIGITAL', qtdTotalAssinatura);
      await this.lacunaService.iniciaPreAutorizacaoAssinatura(pCertificado, qtdTotalAssinatura);
      const encodedCert = await this.lacunaService.retornaCertificadoEncoded(pCertificado);

      if (pModoAssinatura === ModoAssinaturaDigital.PARALELO_POR_DOCUMENTO) {
        await this.assinarDocumentosParalelos(documentosAssinar, pCertificado, encodedCert);
      } else {
        await this.assinarDocumentosSequencial(documentosAssinar, pCertificado, encodedCert);
      }

      this.loadingSignatureService.hideLoader();
      this.log.printEvent();
      this.log.clearAll();
      return Promise.resolve();
    } catch (error) {
      console.log('ERRO ASSINATURA DIGITAL', error);
      this.log.addEvent({
        method: 'execute',
        tipo: 'ERRO',
        params: {
          pDocumentosComInvolvido,
          pCertificado,
          pModoAssinatura,
        },
        error,
      });
      this.log.printEvent();
      this.log.clearAll();
      this.loadingSignatureService.hideLoader();
      return Promise.reject(error);
    }
  }

  private async assinarDocumentosParalelos(pDocumentos: SignatureDocumentDigital[], pCertificado: Certificate, pCertEncoded: string): Promise<void> {
    this.log.addEvent({
      method: 'assinarDocumentosParalelos',
      tipo: 'ENTRADA',
      params: {
        pDocumentos,
        pCertificado,
        pCertEncoded,
      },
    });
    try {
      const documentosParalelos: Promise<any>[] = [];
      let primeiroDocumento: boolean = true;
      for (let docPosition = 0; docPosition < pDocumentos.length; docPosition += 1) {
        const documentoAtual = pDocumentos[docPosition];

        if (primeiroDocumento) {
          await this.assinarDocumento(documentoAtual, pCertificado, pCertEncoded);
          primeiroDocumento = false;
        } else {
          documentosParalelos.push(this.assinarDocumento(documentoAtual, pCertificado, pCertEncoded));
        }
      }

      await Promise.all(documentosParalelos);
      this.log.addEvent({
        method: 'assinarDocumentosParalelos',
        tipo: 'SAIDA',
        params: {
          pDocumentos,
          pCertificado,
          pCertEncoded,
        },
      })
    } catch (error) {
      this.log.addEvent({
        method: 'assinarDocumentosParalelos',
        tipo: 'ERRO',
        params: {
          pDocumentos,
          pCertificado,
          pCertEncoded,
        },
        error,
      });
      return Promise.reject(error);
    }
  }

  private async assinarDocumentosSequencial(pDocumentos: SignatureDocumentDigital[], pCertificado: Certificate, pCertEncoded: string): Promise<void> {
    this.log.addEvent({
      method: 'assinarDocumentosSequencial',
      tipo: 'ENTRADA',
      params: {
        pDocumentos,
        pCertificado,
        pCertEncoded,
      },
    });
    try {
      for (let docPosition = 0; docPosition < pDocumentos.length; docPosition += 1) {
        const documentoAtual = pDocumentos[docPosition];
        for (let involvPosition = 0; involvPosition < documentoAtual.involveds.length; involvPosition += 1) {
          const envolvido = documentoAtual.involveds[involvPosition];
          const qtdAssinaturasRealizadas = envolvido.signedAmount;
          const qtdAssinaturasRealizar = envolvido.title.split(',').length;

          if (qtdAssinaturasRealizadas >= qtdAssinaturasRealizar) {
            continue;
          }

          for (let titlePosition = qtdAssinaturasRealizadas; titlePosition < qtdAssinaturasRealizar; titlePosition += 1) {
            await this.assinar(documentoAtual, envolvido, pCertificado, pCertEncoded);
          }
        }
      }

      this.log.addEvent({
        method: 'assinarDocumentosSequencial',
        tipo: 'SAIDA',
        params: {
          pDocumentos,
          pCertificado,
          pCertEncoded,
        },
      });
    } catch (error) {
      this.log.addEvent({
        method: 'assinarDocumentosSequencial',
        tipo: 'ERRO',
        params: {
          pDocumentos,
          pCertificado,
          pCertEncoded,
        },
        error,
      });
      return Promise.reject(error);
    }
  }

  private async assinarDocumento(pDocumento: SignatureDocumentDigital, pCertificado: Certificate, pCertEncoded: string): Promise<void> {
    this.log.addEvent({
      method: 'assinarDocumento',
      tipo: 'ENTRADA',
      params: {
        pDocumento,
        pCertificado,
        pCertEncoded,
      },
    });
    try {
      for (const envolvido of pDocumento.involveds) {
        for (let i = 0; i < envolvido.qtdAssinaturasRealizar; i += 1) {
          await this.assinar(pDocumento, envolvido, pCertificado, pCertEncoded);
        }
      }
      this.log.addEvent({
        method: 'assinarDocumento',
        tipo: 'SAIDA',
        params: {
          pDocumento,
          pCertificado,
          pCertEncoded,
        },
      });
    } catch (error) {
      this.log.addEvent({
        method: 'assinarDocumento',
        tipo: 'ERRO',
        params: {
          pDocumento,
          pCertificado,
          pCertEncoded,
        },
        error,
      });
      return Promise.reject(error);
    }
  }

  private async assinar(pDocumento: SignatureDocumentDigital, pInvolved: SignatureInvolved, pCertificado: Certificate, pCertEncoded: string): Promise<void> {
    this.log.addEvent({
      method: 'assinar',
      tipo: 'ENTRADA',
      params: {
        pDocumento,
        pInvolved,
        pCertificado,
        pCertEncoded,
      },
    });
    try {
      const startResponse = await this.signatureHttpService.assinarDigitalStart({
        certificate: pCertEncoded,
        certificateRaw: pCertificado,
        guid: pDocumento.docGuid,
        involvedGuid: pInvolved.guid,
        signatureId: pInvolved.signatureId,
      });
      if (startResponse.data === undefined) {
        throw new Error('Não foi possivel iniciar a assinatura do documento');
      }

      const assinaturaLacunaResponse = await this.lacunaService.assinarDocumento(pCertificado, startResponse.data);
      if (assinaturaLacunaResponse.sucesso === false || assinaturaLacunaResponse.assinatura === undefined) {
        await this.signatureHttpService.assinarDigitalCancelar({ guid: pDocumento.docGuid });
        throw new Error(assinaturaLacunaResponse.err || '');
      }

      const completeResponse = await this.signatureHttpService.assinarDigitalComplete({
        docGuid: pDocumento.docGuid,
        involvedGuid: pInvolved.guid,
        signatureId: pInvolved.signatureId,
        transferFile: startResponse.data.transferFile,
        signature: assinaturaLacunaResponse.assinatura,
        certificate: pCertificado.rawCertificate.pkiBrazil,
      });

      if (completeResponse.success === false) {
        throw new Error(completeResponse.message ?? 'Não foi possivel iniciar a assinatura do documento');
      }

      this.loadingSignatureService.incrementQtdSignature();
      this.log.addEvent({
        method: 'assinar',
        tipo: 'SAIDA',
        params: {
          pDocumento,
          pInvolved,
          pCertificado,
          pCertEncoded,
        },
      });
      return Promise.resolve();
    } catch (error) {
      console.log(error);
      this.log.addEvent({
        method: 'assinar',
        tipo: 'ERRO',
        params: {
          pDocumento,
          pInvolved,
          pCertificado,
          pCertEncoded,
        },
        error,
      });
      return Promise.reject(error);
    }
  }

  public validarDigital(pInvolved: SignatureInvolved, pCertificado: Certificate): void {
    this.log.addEvent({
      method: 'validarDigital',
      tipo: 'ENTRADA',
      params: {
        pInvolved,
        pCertificado,
      },
    });
    const isCpfCertIgualInvolvedCpf = pCertificado.cpf === pInvolved.cpf;
    const isCnpjCertIgualInvolvedCnpj = pCertificado.isLegalEntity
      ? pCertificado.cnpj === pInvolved.cnpj
      : true; // Se nao for e-CNPJ, nao precisa comparar.

    if (isCpfCertIgualInvolvedCpf === false || isCnpjCertIgualInvolvedCnpj === false) {
      throw new Error(this.gerarErroCertificado(pCertificado, pInvolved));
    }

    const isNaoAssinado = pInvolved.status === SignStatusEnum.naoAssinado;
    if (pCertificado.isPhysicalPerson) {
      const isAssinantePf = pInvolved.signerType === TipoPessoaLetraEnum.pessoaFisica;
      const isAssinantePjComECPF =
        pInvolved.signerType === TipoPessoaLetraEnum.pessoaJuridica
        && !!pInvolved.useECNPJ === false;

      const isPermiteAssinar = isNaoAssinado && (isAssinantePf || isAssinantePjComECPF);
      if (isPermiteAssinar === false) {
        throw new Error('Atenção: Assinatura não permite com e-CPF, assine com e-CNPJ');
      }
    }
    if (pCertificado.isLegalEntity && pInvolved.signerType === TipoPessoaLetraEnum.pessoaJuridica) {
      const permiteAssinar = isNaoAssinado && pInvolved.signerType === TipoPessoaLetraEnum.pessoaJuridica && !!pInvolved.useECPF === false;
      if (permiteAssinar === false) {
        throw new Error('Atenção: Assinatura não permite com e-CNPJ, assine com e-CPF');
      }
    }

    this.log.addEvent({
      method: 'validarDigital',
      tipo: 'SAIDA',
      params: {
        pInvolved,
        pCertificado,
      },
    });
  }

  private getDocumentosAssinarDigital(pDocumentos: SignatureDocumentDigital[], pCertificado: Certificate): SignatureDocumentDigital[] {
    this.log.addEvent({
      method: 'getDocumentosAssinarDigital',
      tipo: 'ENTRADA',
      params: {
        pDocumentos,
        pCertificado,
      },
    });
    const documentosAssinarComEnvolvidos: SignatureDocumentDigital[] = [];
    for (const documento of pDocumentos) {
      const involveds: SignatureInvolved[] = [];

      if (documento.involveds.length === 0) {
        continue;
      }

      const involvedsComMemsmoCpf = documento.involveds.filter((inv) => inv.cpf === pCertificado.cpf);
      if (involvedsComMemsmoCpf.length === 0) {        
        this.validarDigital(documento.involveds[0], pCertificado);
      }
      let isDocumentoInvolvidoAssinado: boolean = true;

      for (const involved of involvedsComMemsmoCpf) {
        const isDocumentoNaoAssinar = documento.assinar === false;
        const isDocumentoJaAssinado = involved.status !== SignStatusEnum.naoAssinado;
        if (isDocumentoJaAssinado || isDocumentoNaoAssinar) {
          continue;
        }
        isDocumentoInvolvidoAssinado = false;
        // this.validarDigital(involved, pCertificado);

        let isAssinar: boolean = false;
        if (pCertificado.isLegalEntity && involved.signerType === TipoPessoaLetraEnum.pessoaJuridica) {
          if (pCertificado.cpf === involved.cpf && pCertificado.cnpj === involved.cnpj && involved.useECPF === false) {
            isAssinar = true;
          }
        }
        if (pCertificado.isPhysicalPerson && involved.useECNPJ === false) {
          if (pCertificado.cpf === involved.cpf) {
            isAssinar = true;
          }
        }
        if (isAssinar) {
          involveds.push(Object.assign({}, involved));
        }
      }
      if (involveds.length) {
        documentosAssinarComEnvolvidos.push(new SignatureDocumentDigital(documento.docGuid, documento.nomeDocumento, documento.status, true, involveds));
      } else if (isDocumentoInvolvidoAssinado === false) {
        documento.involveds.forEach(pInv => this.validarDigital(pInv, pCertificado));
      }
    }

    this.log.addEvent({
      method: 'getDocumentosAssinarDigital',
      tipo: 'SAIDA',
      params: {
        documentosAssinarComEnvolvidos,
      },
      returnData: documentosAssinarComEnvolvidos,
    });
    return documentosAssinarComEnvolvidos;
  }

  private getQtdAssinaturasTotal(pDocumentosAssinar: SignatureDocumentDigital[]): number {
    this.log.addEvent({
      method: 'getQtdAssinaturasTotal',
      tipo: 'ENTRADA',
      params: {
        pDocumentosAssinar,
      },
    });
    let qtd: number = 0;
    pDocumentosAssinar.forEach(pDoc => pDoc.involveds.forEach(pInv => qtd += pInv.qtdAssinaturasRealizar));

    this.log.addEvent({
      method: 'getQtdAssinaturasTotal',
      tipo: 'SAIDA',
      params: {
        pDocumentosAssinar,
      },
      returnData: qtd,
    });
    return qtd;
  }

  private gerarErroCertificado(pCertificado: Certificate, pEnvolvido: SignatureInvolved): string {
    this.log.addEvent({
      method: 'gerarErroCertificado',
      tipo: 'ENTRADA',
      params: {
        pCertificado,
        pEnvolvido,
      },
    })
    const cpfInvolved = '***' + pEnvolvido.cpf.substring(3, 12);
    const cpfCertifate = '***' + pCertificado.cpf.substring(3, 12);

    let mensagem = '<span style="font-size:14px">O certificado escolhido não representa o envolvido.</span>';
    mensagem += '<div class="pt-3 pb-3" style="font-size:12px;border-top: 1px solid #B9C0C7;border-bottom: 1px solid #B9C0C7;">';
    mensagem += '<span>Envolvido a assinar: <strong>CPF ' + cpfInvolved;
    if (pEnvolvido.cnpj !== null) {
      const cnpjInvolved = '****' + pEnvolvido.cnpj.substring(4, 20);
      mensagem += ', CNPJ ' + cnpjInvolved;
    }
    mensagem += '</strong></span>';

    mensagem += '<br><span>Certificado selecionado:<strong>CPF ' + cpfCertifate;
    if (pCertificado.cnpj !== undefined && pCertificado.cnpj !== null) {
      const cnpjCertificate = '****' + pCertificado.cnpj.substring(4, 20);
      mensagem += ', CNPJ ' + cnpjCertificate;
    }
    mensagem += '</strong></span>';
    mensagem += '</div>';
    mensagem += '<div style="font-size:12px">Selecione um certificado que tenha os dados do envolvido ou solicite um novo envio com os dados corretos do envolvido para quem solicitou sua assinatura.</div>';

    this.log.addEvent({
      method: 'gerarErroCertificado',
      tipo: 'SAIDA',
      params: {
        pCertificado,
        pEnvolvido,
      },
      returnData: mensagem,
    })
    return mensagem;
  }
}
